import PropTypes from 'prop-types';

/**
 * The on/off switch in a table row — active or inactive, available or busy.
 *
 * Copied from the Super Admin master tables, which is where this control was
 * settled: 44px by 24px, a 20px white knob, and the shop's own colour when on.
 *
 * Ten tables in this console each drew their own, and they had split three ways
 * on the one thing a switch is for — saying it is on. Six used emerald green,
 * two used a hard-coded `green-500` with a `gray-300` track, and only two used
 * the shop's colour. A shop whose theme is blue or purple therefore had an
 * emerald switch in Suppliers and a themed one in Categories, on the same
 * screen, meaning the same thing. Green also carries its own suggestion —
 * healthy, passed, good — which is wrong here: inactive is not a failure, it is
 * a choice somebody made.
 *
 * They were also all built out of a bare `<button>`, which tells assistive
 * software nothing about being a switch or about which way it is thrown. This
 * one is a `role="switch"` with `aria-checked`, so it is announced as "Ravi
 * Kumar, switch, on" rather than as an unlabelled button.
 */

export const StatusToggle = ({ on, onChange, label, disabled = false, title }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    // The hint says what pressing it will do, not what the state is — the
    // switch itself already shows that.
    title={title || (disabled ? label : `Click to mark as ${on ? 'Inactive' : 'Active'}`)}
    className={`flex items-center outline-none group/switch ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  >
    <span
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        on
          ? 'bg-[var(--theme-primary)]'
          : `bg-slate-300 ${disabled ? '' : 'group-hover/switch:bg-slate-400'}`
      }`}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out"
        style={{ transform: on ? 'translateX(20px)' : 'translateX(2px)' }}
      />
    </span>
  </button>
);

StatusToggle.propTypes = {
  on: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  /** Name the row — "Ravi Kumar", "Main Branch". Read aloud with the state. */
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  /** Overrides the hover hint. Use it to say why a switch cannot be thrown. */
  title: PropTypes.string,
};
